import { Line } from 'recharts';

import { GAP_BRIDGE_LINE_PROPS } from './gapBridge';
import { renderIsolatedDot } from './renderIsolatedDot';
import type { FillPolicy } from '../../utils/padSeriesToPeriod';

export const CHART_COLOR = 'var(--chart-1)';
export const COMPARISON_CHART_COLOR = 'var(--chart-2)';

/**
 * The primary + comparison `<Line>` stack shared by every analytics timeseries
 * chart, so the pieces whose props must stay mutually consistent live in one
 * place:
 *
 * - Gap metrics draw a dashed bridge *under* each solid line, and bridge and
 *   solid must both be `linear` so they coincide on data runs and the dashes
 *   show only across gaps (see gapBridge.tsx). Count metrics have no bridge,
 *   so they keep `monotone`.
 * - The comparison series (`comparisonValue`) gets the same treatment in its
 *   own colour.
 *
 * Recharts only recognises literal chart children, so this is a plain function
 * returning `<Line>` elements — invoke it inside `<LineChart>` as
 * `{renderTimeseriesLines({ ... })}`, never render it as a component.
 */
export const renderTimeseriesLines = ({
  fill,
  showComparison,
}: {
  fill: FillPolicy;
  showComparison: boolean;
}) => {
  const lineType = fill === 'gap' ? 'linear' : 'monotone';
  const lines = [];

  if (fill === 'gap') {
    lines.push(
      <Line key="value-bridge" dataKey="value" stroke={CHART_COLOR} {...GAP_BRIDGE_LINE_PROPS} />
    );
  }
  lines.push(
    <Line
      key="value"
      type={lineType}
      dataKey="value"
      stroke={CHART_COLOR}
      strokeWidth={2}
      dot={renderIsolatedDot}
      activeDot={{ r: 5, fill: CHART_COLOR, stroke: 'var(--background)', strokeWidth: 2 }}
    />
  );
  if (showComparison && fill === 'gap') {
    lines.push(
      <Line
        key="comparison-bridge"
        dataKey="comparisonValue"
        stroke={COMPARISON_CHART_COLOR}
        {...GAP_BRIDGE_LINE_PROPS}
      />
    );
  }
  if (showComparison) {
    lines.push(
      <Line
        key="comparison"
        type={lineType}
        dataKey="comparisonValue"
        stroke={COMPARISON_CHART_COLOR}
        strokeWidth={2}
        dot={false}
        activeDot={false}
      />
    );
  }
  return lines;
};
